import React, { useEffect, useState } from "react";
import { Table, Modal } from "antd";
import FETCH from "../../../utils/fetch";
import NewLoader from "../../../components/styled-components/loader/NewLoader";
import moment from "moment";
import { MODULE_API } from "../../../apis";
import swal from "sweetalert";
import { ToolTipCreator } from "../../../helpers/ToolTipCreator";

export const EducationAssetAssignLiquid = ({
    rows,
    data,
    handleLiquidModuleToUpdate,
    cbFormChange,
    allLiabilities,
    attributeValues,
}) => {
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [cachedData, setCachedData] = useState({});
    const [assetsList, setAssetsList] = useState([]);
    const [goalsWithAssets, setGoalsWithAssets] = useState([]);
    const [liabilities, setLiabilities] = useState(allLiabilities);

    const [total, setTotal] = useState(null);
    const [dataSource, setDataSource] = useState(rows);
    const [count, setCount] = useState((rows && rows.length) + 1 || 1);
    const [assetsToUpdate, setAssetsToUpdate] = useState([]);

    const [totalAmount, setTotalAmount] = useState(0);
    const [totalAmountUsed, setTotalAmountUsed] = useState(0);

    useEffect(() => {
        setDataSource(rows);
        const amountUsed =
            rows && Array.isArray(rows)
                ? rows.reduce(
                    (acc, curr) => acc + parseFloat(curr.amountUseForGoal || 0),
                    0
                )
                : 0;
        const havefromFriends =
            data &&
            data["Amount Received"] &&
            (Array.isArray(data["Amount Received"])
                ? data["Amount Received"].reduce((acc, curr) => acc + curr, 0)
                : 0);
        const totalhave = amountUsed + havefromFriends;
        setTotalAmount(totalhave);
        setTotalAmountUsed(amountUsed);
    }, [rows, data]);

    const handleAdd = async (record) => {
        if (dataSource && dataSource.some((item) => item.assetId === record.id)) {
            swal("Info", "This asset has already been added.", "info");
            return;
        }

        const transformedData = liabilities && calculatePayments(liabilities);
        let result = record && calculateGoalRequirements(record, data, transformedData);

        const havefromFriends =
            data &&
            data["Amount Received"] &&
            (Array.isArray(data["Amount Received"])
                ? data["Amount Received"].reduce((acc, curr) => acc + curr, 0)
                : 0);

        const amountUsed =
            (rows && Array.isArray(rows)
                ? rows.reduce(
                    (acc, curr) => acc + parseFloat(curr.amountUseForGoal || 0),
                    0
                )
                : 0) + parseFloat(havefromFriends || 0);

        const goalNeedToday = parseFloat(
            result.goalNeedToday.replace(/[^0-9.-]+/g, "")
        );

        // For liquid assets, we can decide how much to use. 
        // Default to using what's needed or what's available.
        const available = record.amountAvailable || record["Monetary Value"] || 0;
        const remainingNeed = goalNeedToday - amountUsed;

        if (remainingNeed <= 0) {
            swal("Oops", "Goal Need Already Fulfilled", "error");
            return;
        }

        const amountToUse = Math.min(available, remainingNeed);

        const newData = {
            key: count.toString(),
            assetId: record.id,
            priority: count,
            asset: record && record["Name of Asset"] ? record["Name of Asset"] : null,
            valueOfAssetGoalRate: record && record["Monetary Value"] ? +record["Monetary Value"] : null,
            amountUseForGoal: amountToUse,
            "Amount used in other goal": record && record.sumOfAmountUsed ? record.sumOfAmountUsed : null,
            taxes: record && record["Taxes"] ? record["Taxes"] : null,
            netAmountAfterTax: (totalAmountUsed + amountToUse),
        };

        const updatedDataSource = [...dataSource, newData];
        setDataSource(updatedDataSource);
        setCount(count + 1);
        cbFormChange(updatedDataSource);

        const updatedAssetsToUpdate = [...assetsToUpdate, record.id];
        setAssetsToUpdate(updatedAssetsToUpdate);
        handleLiquidModuleToUpdate(updatedAssetsToUpdate, record.goal_id || []);

        if (amountToUse >= remainingNeed) {
            swal("Success", "Goal Fully Funded!", "success");
        } else {
            swal("Success", "Asset Added to Goal", "success");
        }
    };

    const handleDelete = (record) => {
        const matchedData = dataSource && dataSource.find((item) => item.assetId === record.id);
        if (matchedData) {
            const amountToUse = parseFloat(matchedData.amountUseForGoal) || 0;
            const updatedTotalAmountUsed = totalAmountUsed - amountToUse;

            const newData = dataSource
                .filter((item) => item.assetId !== record.id)
                .map((item) => ({
                    ...item,
                    netAmountAfterTax: updatedTotalAmountUsed,
                }));

            setDataSource(newData);
            setTotalAmountUsed(updatedTotalAmountUsed);
            setCount(count - 1);
            cbFormChange(newData);

            const updatedAssetsToUpdate = assetsToUpdate.filter(id => id !== record.id);
            setAssetsToUpdate(updatedAssetsToUpdate);
            handleLiquidModuleToUpdate(updatedAssetsToUpdate, record.goal_id || []);
        }
    };

    const getAssetsList = async (page) => {
        if (cachedData && cachedData[page]) {
            updateStateWithData(cachedData[page]);
            return;
        }
        try {
            setLoading(true);
            const userRole = localStorage.getItem("role") !== "undefined" && JSON.parse(localStorage.getItem("role"));
            const id = JSON.parse(localStorage.getItem("accessId"));
            let datares = await FETCH.post({
                url: "client-module-service/client-module/liquid",
                id: userRole === "Financial Advisor Client" ? `?page=${page}&limit=10&clientId=${id}` : `?page=${page}&limit=10`,
                body: {},
            });
            setLoading(false);

            if (datares && datares.records) {
                setCachedData((prevState) => ({
                    ...prevState,
                    [page]: datares,
                }));
                updateStateWithData(datares);
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    };

    const updateStateWithData = (datares) => {
        if (datares && datares.records) {
            const filteredAssets = datares.records.filter(
                (record) => !record.hasOwnProperty("This Asset is Liquid") || record["This Asset is Liquid"] === true
            );

            const allGoals = datares.goals || [];
            setGoalsWithAssets(allGoals);

            const assetsWithGoalInfo = filteredAssets.map(asset => {
                const monetaryValue = calculateMonetoryValue(asset);
                const matchingGoals = allGoals.filter(goal => asset.goal_id && asset.goal_id.includes(goal.id));

                const sumOfAmountUsed = matchingGoals.reduce((sum, goal) => {
                    const amountArray = goal.assignAssets;
                    const amount = amountArray && amountArray.length > 0
                        ? amountArray.reduce((acc, curr) => acc + parseFloat(curr.amountUseForGoal || 0), 0)
                        : 0;
                    return sum + amount;
                }, 0);

                const amountAvailable = monetaryValue - sumOfAmountUsed;

                return {
                    ...asset,
                    "Monetary Value": +monetaryValue,
                    sumOfAmountUsed,
                    amountAvailable,
                };
            });

            setAssetsList(assetsWithGoalInfo);
        }
        if (datares && datares.pagination) {
            setTotal(datares.pagination.total);
            setTotalPages(Math.ceil(datares.pagination.total / datares.pagination.limit));
        }
    };

    const calculateMonetoryValue = (asset) => {
        const currentDate = data["Goal Date"] ? new Date(data["Goal Date"]) : new Date();
        let Start = (asset["Cost Basis Date"] && new Date(asset["Cost Basis Date"])) || new Date();
        const differenceInMs = currentDate.getTime() - Start.getTime();
        const daysDifference = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));
        let totalDays = daysDifference / 365;

        let Cost = +asset["Cost Basis"] || 0;
        let ReturnRate = +asset["Static"] || 0;
        let decimalValue = ReturnRate ? ReturnRate / 100 : 0;
        const amount = 1 + decimalValue / 365;
        let power = Math.pow(amount, totalDays * 365);
        const estimateDiff = Cost * power;

        return estimateDiff > 0 && !isNaN(estimateDiff) ? parseFloat(estimateDiff).toFixed(2) : 0;
    };

    const calculatePayments = (liabilities) => {
        if (!liabilities) return {};
        const result = {};
        liabilities.forEach((item) => {
            const nickname = item["Nickname of Liabilities"];
            let monthlyPayment = parseFloat(String(item["Monthly Payment"] || 0).replace(/[$,]/g, "")) || 0;
            let initialLoanAmount = parseFloat(String(item["Initial Loan Amount"] || 0).replace(/[$,]/g, "")) || 0;
            let interestRate = parseFloat(String(item["Interest Rate"] || 0).replace(/[%]/g, "")) / 100 || 0;
            let startDate = new Date(item["Start Date"] || new Date());
            let endDate = new Date(item["Maturity Date"] || new Date());

            const payments = [];
            let currentDate = new Date(startDate);
            const monthlyInterestRate = interestRate / 12;
            let remainingBalance = initialLoanAmount;

            let iteration = 0;
            while (currentDate <= endDate && remainingBalance > 0 && iteration < 1200) {
                const interestPaid = remainingBalance * monthlyInterestRate;
                const principalPaid = Math.min(monthlyPayment - interestPaid, remainingBalance);
                const valuePayable = Math.round((interestPaid + principalPaid) * 100) / 100;
                if (valuePayable > 0) {
                    payments.push({ date: moment(currentDate).format("MM-DD-YYYY"), valuepayable: valuePayable });
                }
                remainingBalance -= principalPaid;
                currentDate.setMonth(currentDate.getMonth() + 1);
                iteration++;
            }
            result[nickname] = payments;
        });
        return result;
    };

    const calculateGoalRequirements = (record, data, amortization) => {
        const todaysDate = new Date();
        const GoalDate = data["Goal Date"] ? new Date(data["Goal Date"]) : new Date();
        const GoalNeedToday = data["Dollar Amount Needed"] || 0;
        const GoalInflationRate = 0.05; // Default 5% for Education

        const years = (GoalDate - todaysDate) / (1000 * 60 * 60 * 24 * 365);
        const result = GoalNeedToday * Math.pow(1 + GoalInflationRate, years);

        const equityForGoal = (record["Monetary Value"] || 0) - (record["Taxes"] || 0);

        return {
            equityForGoal: `$${parseFloat(equityForGoal).toLocaleString()}`,
            goalNeedToday: `$${result.toLocaleString()}`,
        };
    };

    useEffect(() => {
        getAssetsList(currentPage);
    }, [currentPage]);

    const colsLiquidAssets = [
        {
            title: "Asset",
            dataIndex: "Name of Asset",
            render: (record) => record || "--------",
        },
        {
            title: "Amount Available",
            render: (fileName, record) => record ? `$${parseFloat(record.amountAvailable || record["Monetary Value"] || 0).toLocaleString()}` : "N/A",
        },
        {
            title: "Used in Other Goals",
            dataIndex: "sumOfAmountUsed",
            render: (record) => record ? `$${parseFloat(record).toLocaleString()}` : "$0",
        },
        {
            title: "ACTION",
            render: (record) => {
                let isSelected = dataSource && dataSource.some((item) => item.assetId === record.id);
                return (
                    <button
                        className="saveStatusbutton"
                        style={{
                            width: "100%",
                            fontWeight: "bold",
                            background: isSelected ? "#E13C25" : "",
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            isSelected ? handleDelete(record) : handleAdd(record);
                        }}
                    >
                        {isSelected ? "Selected" : "Select"}
                    </button>
                );
            },
        },
    ];

    return (
        <div className="assetAssignTable" style={{ position: "relative" }}>
            <div className="goalSubFormTable">
                <div style={{ display: "flex", alignItems: "baseline", columnGap: "3px" }}>
                    <h3 className="incomecardheading">Education Goal: Liquid Assets Assignment</h3>
                    <ToolTipCreator name={"Goals_Liquid_Assets"} />
                </div>
                <Table
                    columns={colsLiquidAssets}
                    dataSource={assetsList}
                    pagination={{
                        total: totalPages * 10,
                        current: currentPage,
                        onChange: (page) => setCurrentPage(page),
                        pageSize: 10,
                    }}
                />
            </div>
            <NewLoader isLoading={loading} />
        </div>
    );
};
